import React, { useState, useEffect } from 'react';
import axios from 'axios';


const API_URL = process.env.REACT_APP_API_URL;

const PaymentTransactionManagement = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    paymentMethod: '',
    startDate: '',
    endDate: '',
    page: 1,
    limit: 20
  });
  const [pagination, setPagination] = useState({});
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchTransactions();
  }, [filters]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) params.append(key, filters[key]);
      });

      const response = await axios.get(`${API_URL}/api/admin/payments/transactions?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setTransactions(response.data.transactions);
      setPagination(response.data.pagination);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      alert('取引履歴の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPayment = async (transactionId) => {
    if (!window.confirm('この支払いを確認しますか？')) return;
    
    setActionLoading(transactionId);
    try {
      await axios.post(`${API_URL}/api/admin/payments/transactions/${transactionId}/confirm`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      alert('支払いを確認しました');
      fetchTransactions();
    } catch (err) {
      alert('支払い確認に失敗しました');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelPayment = async (transactionId) => {
    if (!window.confirm('この取引をキャンセルしますか？')) return;
    
    setActionLoading(transactionId);
    try {
      await axios.post(`${API_URL}/api/admin/payments/transactions/${transactionId}/cancel`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      alert('取引をキャンセルしました');
      fetchTransactions();
    } catch (err) {
      alert('取引キャンセルに失敗しました');
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#f39c12';
      case 'paid': return '#27ae60';
      case 'cancelled': return '#e74c3c';
      case 'failed': return '#e74c3c';
      default: return '#95a5a6';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return '処理中';
      case 'paid': return '支払い済み';
      case 'cancelled': return 'キャンセル';
      case 'failed': return '失敗';
      default: return status;
    }
  };

  const getPaymentMethodText = (method) => {
    switch (method) {
      case 'bank_transfer': return '銀行振込';
      case 'smart_pit': return 'SmartPit';
      default: return method;
    }
  };

  if (loading) return <div>読み込み中...</div>;

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: 20 }}>
      <h2>支払い取引管理</h2>

      {/* Filters */}
      <div style={{ 
        background: '#f8f9fa', 
        padding: '20px', 
        borderRadius: '8px', 
        marginBottom: '20px',
        border: '1px solid #dee2e6'
      }}>
        <h4 style={{ margin: '0 0 15px 0' }}>フィルター</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>ステータス</label>
            <select 
              value={filters.status} 
              onChange={e => setFilters({...filters, status: e.target.value, page: 1})}
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            >
              <option value="">すべて</option>
              <option value="pending">処理中</option>
              <option value="paid">完了</option>
              <option value="cancelled">キャンセル</option>
              <option value="failed">失敗</option>
            </select>
          </div>
          
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>支払い方法</label>
            <select 
              value={filters.paymentMethod} 
              onChange={e => setFilters({...filters, paymentMethod: e.target.value, page: 1})}
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            >
              <option value="">すべて</option>
              <option value="bank_transfer">銀行振込</option>
              <option value="smart_pit">Smart Pit</option>
            </select>
          </div>
          
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>開始日</label>
            <input 
              type="date" 
              value={filters.startDate} 
              onChange={e => setFilters({...filters, startDate: e.target.value, page: 1})}
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          </div>
          
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>終了日</label>
            <input 
              type="date" 
              value={filters.endDate} 
              onChange={e => setFilters({...filters, endDate: e.target.value, page: 1})}
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ 
          width: '100%', 
          background: '#fff', 
          borderRadius: '8px', 
          overflow: 'hidden',
          borderCollapse: 'collapse'
        }}>
          <thead>
            <tr style={{ background: '#34495e', color: 'white' }}>
              <th style={{ padding: '12px', textAlign: 'left' }}>日時</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>取引ID</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>ユーザー</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>方法</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>金額</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>ポイント</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>ステータス</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {transactions.length === 0 && (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', padding: '20px', color: '#7f8c8d' }}>
                  取引履歴がありません
                </td>
              </tr>
            )}
            {transactions.map(transaction => (
              <tr key={transaction._id} style={{ borderBottom: '1px solid #ecf0f1' }}>
                <td style={{ padding: '12px' }}>
                  {new Date(transaction.createdAt).toLocaleString('ja-JP')}
                </td>
                <td style={{ padding: '12px', fontFamily: 'monospace', fontSize: '12px' }}>
                  {transaction.transactionId}
                </td>
                <td style={{ padding: '12px' }}>
                  <div>
                    <strong>{transaction.user?.name || transaction.user?.userID}</strong>
                    <br />
                    <small style={{ color: '#7f8c8d' }}>{transaction.user?.email}</small>
                  </div>
                </td>
                <td style={{ padding: '12px' }}>
                  {getPaymentMethodText(transaction.paymentMethod)}
                </td>
                <td style={{ padding: '12px', fontWeight: 'bold' }}>
                  ¥{transaction.amount.toLocaleString()}
                </td>
                <td style={{ padding: '12px', fontWeight: 'bold', color: '#27ae60' }}>
                  {transaction.points.toLocaleString()} pt
                </td>
                <td style={{ padding: '12px' }}>
                  <span style={{
                    backgroundColor: getStatusColor(transaction.status),
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px'
                  }}>
                    {getStatusText(transaction.status)}
                  </span>
                </td>
                <td style={{ padding: '12px' }}>
                  {transaction.status === 'pending' && (
                    <div style={{ display: 'flex', gap: '5px' }}>
                      <button 
                        onClick={() => handleConfirmPayment(transaction.transactionId)}
                        disabled={actionLoading === transaction.transactionId}
                        style={{
                          background: '#27ae60',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          cursor: actionLoading === transaction.transactionId ? 'not-allowed' : 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        {actionLoading === transaction.transactionId ? '処理中...' : '確認'}
                      </button>
                      <button 
                        onClick={() => handleCancelPayment(transaction.transactionId)}
                        disabled={actionLoading === transaction.transactionId}
                        style={{
                          background: '#e74c3c',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          cursor: actionLoading === transaction.transactionId ? 'not-allowed' : 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        {actionLoading === transaction.transactionId ? '処理中...' : 'キャンセル'}
                      </button>
                    </div>
                  )}
                  {transaction.status === 'paid' && (
                    <small style={{ color: '#27ae60' }}>
                      確認済み ({transaction.confirmedBy?.name || 'Admin'})
                    </small>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {(pagination.totalPages > 1 || pagination.pages > 1) && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          gap: '10px',
          marginTop: '20px'
        }}>
          <button 
            onClick={() => setFilters({...filters, page: Math.max(1, filters.page - 1)})}
            disabled={filters.page <= 1}
            style={{
              background: filters.page <= 1 ? '#95a5a6' : '#3498db',
              color: 'white',
              border: 'none',
              padding: '8px 12px',
              borderRadius: '4px',
              cursor: filters.page <= 1 ? 'not-allowed' : 'pointer'
            }}
          >
            前へ
          </button>
          
          <span>
            {filters.page} / {(pagination.totalPages || pagination.pages)} ページ
          </span>
          
          <button 
            onClick={() => setFilters({...filters, page: Math.min((pagination.totalPages || pagination.pages), filters.page + 1)})}
            disabled={filters.page >= (pagination.totalPages || pagination.pages)}
            style={{
              background: filters.page >= (pagination.totalPages || pagination.pages) ? '#95a5a6' : '#3498db',
              color: 'white',
              border: 'none',
              padding: '8px 12px',
              borderRadius: '4px',
              cursor: filters.page >= (pagination.totalPages || pagination.pages) ? 'not-allowed' : 'pointer'
            }}
          >
            次へ
          </button>
        </div>
      )}

      {/* Summary */}
      <div style={{ 
        background: '#f8f9fa', 
        padding: '15px', 
        borderRadius: '8px', 
        marginTop: '20px',
        border: '1px solid #dee2e6'
      }}>
        <h4 style={{ margin: '0 0 10px 0' }}>サマリー</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px' }}>
          <div>
            <strong>総取引数:</strong> {pagination.total || 0}
          </div>
          <div>
            <strong>処理中:</strong> {transactions.filter(t => t.status === 'pending').length}
          </div>
          <div>
            <strong>完了:</strong> {transactions.filter(t => t.status === 'paid').length}
          </div>
          <div>
            <strong>キャンセル:</strong> {transactions.filter(t => t.status === 'cancelled').length}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentTransactionManagement; 